import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  AppBar,
  Toolbar,
  Chip,
  Stack,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Public as PublicIcon,
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  EmojiEvents as TrophyIcon,
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  HourglassTop as HourglassIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";
import { useAuth } from "../contexts/AuthContext";
import FormattedTextBlock from "../components/FormattedTextBlock";
import PasswordTextField from "../components/PasswordTextField";

type View = "landing" | "register" | "pending";
type RegistrationState = "open" | "not-yet-open" | "closed";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** Parse a Firestore timestamp or ISO string into a JS Date */
function toDate(d: any): Date | null {
  if (!d) return null;
  try {
    if (d instanceof Date) return d;
    if (d._seconds) return new Date(d._seconds * 1000);
    if (d.seconds) return new Date(d.seconds * 1000);
    const parsed = new Date(d);
    if (!isNaN(parsed.getTime())) return parsed;
  } catch { /* fall through */ }
  return null;
}

function computeTimeLeft(target: Date): TimeLeft {
  const diff = Math.max(0, target.getTime() - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

export default function TournamentShareLanding() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();
  const { playerUser } = useAuth();

  const primaryColor =
    (currentOrg?.slug === "padel-eagle"
      ? "#743444"
      : currentOrg?.branding?.primaryColor || "#1a237e");

  const isPE = currentOrg?.slug === "padel-eagle";
  const buttonSx = isPE
    ? { bgcolor: "#E49421", color: "#fff", "&:hover": { bgcolor: "#74586C" } }
    : { bgcolor: primaryColor, "&:hover": { filter: "brightness(0.9)" } };

  const [view, setView] = useState<View>("landing");
  const [tournament, setTournament] = useState<any>(null);
  const [loadingTournament, setLoadingTournament] = useState(true);
  const [tournamentError, setTournamentError] = useState("");
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);

  // Register form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    level: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [pendingMessage, setPendingMessage] = useState("");

  // If player is already logged in, redirect to their dashboard
  useEffect(() => {
    if (playerUser) {
      // Save the tournament page to redirect back if needed
      localStorage.setItem("redirectAfterLogin", "/player/dashboard");
      navigate("/player/dashboard", { replace: true });
    }
  }, [playerUser, navigate]);

  // Fetch tournament info
  useEffect(() => {
    if (!tournamentId) return;
    const load = async () => {
      setLoadingTournament(true);
      try {
        const res = await apiRequest(`/api/tournaments/${tournamentId}`);
        if (res.ok) {
          setTournament(await res.json());
        } else {
          setTournamentError("Tournament not found.");
        }
      } catch {
        setTournamentError("Could not load tournament details.");
      } finally {
        setLoadingTournament(false);
      }
    };
    load();
  }, [tournamentId]);

  const handleLoginClick = () => {
    // After login, player lands on dashboard where they can register for the tournament
    localStorage.setItem("redirectAfterLogin", "/player/dashboard");
    navigate("/player/login");
  };

  // Derive registration state from tournament data
  const getRegistrationState = useCallback((): RegistrationState => {
    if (!tournament) return "not-yet-open";
    if (tournament.status === "registration") return "open";
    const now = Date.now();
    const closeDate = toDate(tournament.registrationCloseDate);
    if (closeDate && closeDate.getTime() < now) return "closed";
    const openDate = toDate(tournament.registrationOpenDate);
    if (openDate && openDate.getTime() > now) return "not-yet-open";
    if (openDate && openDate.getTime() <= now) return "closed";
    return "not-yet-open";
  }, [tournament]);

  const registrationState = tournament ? getRegistrationState() : null;

  // Countdown clock – ticks while registration has not yet opened
  useEffect(() => {
    if (!tournament || registrationState !== "not-yet-open") return;
    const openDate = toDate(tournament.registrationOpenDate);
    if (!openDate) return;
    setTimeLeft(computeTimeLeft(openDate));
    const timer = setInterval(() => {
      const tl = computeTimeLeft(openDate);
      setTimeLeft(tl);
      if (tl.days === 0 && tl.hours === 0 && tl.minutes === 0 && tl.seconds === 0) {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [tournament, registrationState]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Usernames must be usable on the login form: ignore characters that aren't allowed
    if (e.target.name === "username" && !/^[a-zA-Z0-9_-]*$/.test(e.target.value)) return;
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!agreedToPrivacy) {
      setFormError("You must agree to the privacy policy to continue.");
      return;
    }
    if (formData.password.length < 8) {
      setFormError("Password must be at least 8 characters long.");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }
    if (!formData.name || !formData.username || !formData.password) {
      setFormError("Name, username and password are required.");
      return;
    }
    if (formData.username.length < 3) {
      setFormError("Username must be at least 3 characters.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiRequest("/api/player-requests/account-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          level: formData.level || null,
          username: formData.username,
          password: formData.password,
          requestType: "tournament",
          pendingTournamentId: tournamentId || null,
        }),
      });

      if (res.ok) {
        setPendingMessage(
          tournament
            ? `Your registration request has been submitted! Once an admin approves your account, you'll be able to log in and register for "${tournament.name}".`
            : "Your registration request has been submitted! Once an admin approves your account you can log in and register for the tournament.",
        );
        setView("pending");
      } else {
        const data = await res.json();
        setFormError(data.error || "Failed to submit registration.");
      }
    } catch {
      setFormError("Failed to submit registration. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d: any) => {
    const date = toDate(d);
    if (!date) return "";
    return date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  };

  const formatDateTime = (d: any) => {
    const date = toDate(d);
    if (!date) return "";
    return date.toLocaleString(undefined, {
      day: "numeric", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  // ── Shared tournament banner ─────────────────────────────────────────────
  const renderBanner = () =>
    tournament ? (
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderLeft: `6px solid ${primaryColor}`,
          background: isPE ? "#F9F4EB" : "linear-gradient(135deg, #fff 0%, #f9f9f9 100%)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <TrophyIcon sx={{ color: primaryColor }} />
          <Typography variant="h5" fontWeight="bold">
            {tournament.name}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
          {tournament.level && (
            <Chip label={tournament.level} size="small" color="primary" variant="outlined" />
          )}
          {tournament.venue && (
            <Chip label={`📍 ${tournament.venue}`} size="small" variant="outlined" />
          )}
          {tournament.startDate && (
            <Chip label={`📅 ${formatDate(tournament.startDate)}`} size="small" variant="outlined" />
          )}
        </Stack>
        {tournament.description && (
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 1.5, textTransform: "none" }}
            onClick={() => setIsInfoDialogOpen(true)}
          >
            Show info
          </Button>
        )}
      </Paper>
    ) : null;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isPE ? "#FAFAFA" : "#f5f5f5" }}>
      {/* AppBar */}
      <AppBar position="static" elevation={2} sx={{ bgcolor: primaryColor }}>
        <Toolbar>
          <TrophyIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {currentOrg?.name || "Tournament"}
          </Typography>
          <Button color="inherit" startIcon={<PublicIcon />} onClick={() => navigate("/")}>
            Home
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 5 }}>
        {loadingTournament ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tournamentError ? (
          <Alert severity="error">{tournamentError}</Alert>
        ) : (
          <>
            {renderBanner()}

            {/* ───── NOT YET OPEN: COUNTDOWN ───── */}
            {registrationState === "not-yet-open" && (
              <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
                <HourglassIcon sx={{ fontSize: 64, color: primaryColor, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Registration opens soon
                </Typography>
                {toDate(tournament.registrationOpenDate) ? (
                  <>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Registration opens on{" "}
                      <strong>{formatDateTime(tournament.registrationOpenDate)}</strong>
                    </Typography>

                    {/* Countdown blocks */}
                    <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                      {[
                        { value: timeLeft.days, label: "Days" },
                        { value: timeLeft.hours, label: "Hours" },
                        { value: timeLeft.minutes, label: "Min" },
                        { value: timeLeft.seconds, label: "Sec" },
                      ].map(({ value, label }) => (
                        <Box key={label}>
                          <Paper
                            elevation={4}
                            sx={{
                              width: 72,
                              height: 72,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: primaryColor,
                              borderRadius: 2,
                            }}
                          >
                            <Typography
                              variant="h4"
                              fontWeight="bold"
                              color="white"
                              fontFamily="monospace"
                            >
                              {pad(value)}
                            </Typography>
                          </Paper>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {label}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Registration is not yet open. Check back later!
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{" "}
                  <MuiLink
                    component="button"
                    onClick={handleLoginClick}
                    underline="hover"
                    sx={{ cursor: "pointer", color: primaryColor }}
                  >
                    Log in
                  </MuiLink>
                </Typography>
              </Paper>
            )}

            {/* ───── REGISTRATION CLOSED ───── */}
            {registrationState === "closed" && (
              <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
                <LockIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Registration is closed
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Registration for <strong>{tournament.name}</strong> is no longer available.
                </Typography>
                {toDate(tournament.registrationCloseDate) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    It closed on{" "}
                    <strong>{formatDateTime(tournament.registrationCloseDate)}</strong>.
                  </Typography>
                )}
                <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
                  You can still create your tournament account now, so you're ready for future invitations or openings.
                </Alert>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                  onClick={() => setView("register")}
                  sx={{ borderColor: primaryColor, color: primaryColor, py: 1.5, mb: 2 }}
                >
                  Create Tournament Account
                </Button>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{" "}
                  <MuiLink
                    component="button"
                    onClick={handleLoginClick}
                    underline="hover"
                    sx={{ cursor: "pointer", color: primaryColor }}
                  >
                    Log in to your dashboard
                  </MuiLink>
                </Typography>
              </Paper>
            )}

            {/* ───── REGISTRATION OPEN ───── */}
            {registrationState === "open" && (
              <>
                {/* LANDING */}
                {view === "landing" && (
                  <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom align="center">
                      Want to join this tournament?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                      Choose an option below to get started.
                    </Typography>

                    <Stack spacing={2}>
                      {tournament.description && (
                        <Button
                          variant="outlined"
                          size="large"
                          fullWidth
                          onClick={() => setIsInfoDialogOpen(true)}
                          sx={{ borderColor: primaryColor, color: primaryColor, py: 1.5 }}
                        >
                          Show info
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={<LoginIcon />}
                        onClick={handleLoginClick}
                        sx={{ ...buttonSx, py: 1.5 }}
                      >
                        I already have an account — Log in
                      </Button>

                      <Divider>
                        <Typography variant="caption" color="text.secondary">or</Typography>
                      </Divider>

                      <Button
                        variant="outlined"
                        size="large"
                        fullWidth
                        startIcon={<PersonAddIcon />}
                        onClick={() => setView("register")}
                        sx={{ borderColor: primaryColor, color: primaryColor, py: 1.5 }}
                      >
                        I'm new — Create an account
                      </Button>
                    </Stack>

                    <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 3 }}>
                      New accounts require admin approval before you can log in.
                    </Typography>
                  </Paper>
                )}

                {/* REGISTER FORM */}
                {view === "register" && (
                  <Paper elevation={3} sx={{ p: 4 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setView("landing")}
                      sx={{ mb: 2, color: "text.secondary" }}
                    >
                      ← Back
                    </Button>

                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Create your account
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Once an admin approves your account, you'll be able to log in and register for the tournament.
                    </Typography>

                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

                    <Box component="form" onSubmit={handleRegisterSubmit}>
                      <Stack spacing={2}>
                        <TextField label="Full Name" name="name" value={formData.name} onChange={handleInputChange} required fullWidth size="small" />
                        <TextField label="Email" name="email" type="email" value={formData.email} onChange={handleInputChange} fullWidth size="small" />
                        <TextField label="Phone (optional)" name="phone" value={formData.phone} onChange={handleInputChange} fullWidth size="small" />
                        <TextField label="Skill Level (optional, e.g. 3.5)" name="level" value={formData.level} onChange={handleInputChange} fullWidth size="small" />
                        <Divider />
                        <TextField label="Username" name="username" value={formData.username} onChange={handleInputChange} required fullWidth size="small" helperText="Letters, numbers, hyphens and underscores only (no spaces)" />
                        <TextField
                          label="Password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                          fullWidth
                          size="small"
                          helperText="At least 8 characters"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword((s) => !s)}>
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <PasswordTextField label="Confirm Password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} required fullWidth size="small" />
                        <FormControlLabel
                          control={<Checkbox checked={agreedToPrivacy} onChange={(e) => setAgreedToPrivacy(e.target.checked)} size="small" />}
                          label={
                            <Typography variant="body2">
                              I agree to the{" "}
                              <MuiLink component={Link} to="/privacy" target="_blank">Privacy Policy</MuiLink>
                            </Typography>
                          }
                        />
                        <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", sm: "row" } }}>
                          {tournament?.description && (
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={() => setIsInfoDialogOpen(true)}
                              sx={{ borderColor: primaryColor, color: primaryColor, py: 1.2 }}
                            >
                              Show info
                            </Button>
                          )}
                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={submitting}
                            sx={{ ...buttonSx, py: 1.2 }}
                          >
                            {submitting ? <CircularProgress size={22} color="inherit" /> : "Submit Registration Request"}
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  </Paper>
                )}

                {/* PENDING / SUCCESS */}
                {view === "pending" && (
                  <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h4" sx={{ mb: 2 }}>🎉</Typography>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Request submitted!
                    </Typography>
                    <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                      {pendingMessage}
                    </Alert>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      You will receive a confirmation once your account is approved. After approval, log in and navigate to the tournaments page to complete your registration.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/")}
                      sx={{ borderColor: primaryColor, color: primaryColor }}
                    >
                      Back to Home
                    </Button>
                  </Paper>
                )}
              </>
            )}

            {/* Register form is also available when registration is closed so shared generic links still work */}
            {registrationState === "closed" && view === "register" && (
              <Paper elevation={3} sx={{ p: 4 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setView("landing")}
                  sx={{ mb: 2, color: "text.secondary" }}
                >
                  ← Back
                </Button>

                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Create your account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  You can create your account now. If this tournament is full or closed, admins can still invite you or use you for future tournaments.
                </Typography>

                {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

                <Box component="form" onSubmit={handleRegisterSubmit}>
                  <Stack spacing={2}>
                    <TextField label="Full Name" name="name" value={formData.name} onChange={handleInputChange} required fullWidth size="small" />
                    <TextField label="Email" name="email" type="email" value={formData.email} onChange={handleInputChange} fullWidth size="small" />
                    <TextField label="Phone (optional)" name="phone" value={formData.phone} onChange={handleInputChange} fullWidth size="small" />
                    <TextField label="Skill Level (optional, e.g. 3.5)" name="level" value={formData.level} onChange={handleInputChange} fullWidth size="small" />
                    <Divider />
                    <TextField label="Username" name="username" value={formData.username} onChange={handleInputChange} required fullWidth size="small" helperText="Letters, numbers, hyphens and underscores only (no spaces)" />
                    <TextField
                      label="Password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      fullWidth
                      size="small"
                      helperText="At least 8 characters"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPassword((s) => !s)}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <PasswordTextField label="Confirm Password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} required fullWidth size="small" />
                    <FormControlLabel
                      control={<Checkbox checked={agreedToPrivacy} onChange={(e) => setAgreedToPrivacy(e.target.checked)} size="small" />}
                      label={
                        <Typography variant="body2">
                          I agree to the{" "}
                          <MuiLink component={Link} to="/privacy" target="_blank">Privacy Policy</MuiLink>
                        </Typography>
                      }
                    />
                    <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", sm: "row" } }}>
                      {tournament?.description && (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => setIsInfoDialogOpen(true)}
                          sx={{ borderColor: primaryColor, color: primaryColor, py: 1.2 }}
                        >
                          Show info
                        </Button>
                      )}
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={submitting}
                        sx={{ ...buttonSx, py: 1.2 }}
                      >
                        {submitting ? <CircularProgress size={22} color="inherit" /> : "Submit Registration Request"}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            )}

            {registrationState === "closed" && view === "pending" && (
              <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h4" sx={{ mb: 2 }}>🎉</Typography>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Request submitted!
                </Typography>
                <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                  {pendingMessage}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  You will receive a confirmation once your account is approved. After approval, log in to your dashboard.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/")}
                  sx={{ borderColor: primaryColor, color: primaryColor }}
                >
                  Back to Home
                </Button>
              </Paper>
            )}
          </>
        )}
      </Container>

      <Dialog
        open={isInfoDialogOpen}
        onClose={() => setIsInfoDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{tournament?.name || "Tournament info"}</DialogTitle>
        <DialogContent dividers>
          {tournament?.description ? (
            <FormattedTextBlock text={tournament.description} color="text.primary" />
          ) : (
            <Typography variant="body1">No additional information available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsInfoDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
